import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { AdminScheduleEditor } from "@/components/AdminScheduleEditor";
import { AdminMenuEditor } from "@/components/AdminMenuEditor";
import { toast } from "sonner";
import { RefreshCw, Package, Clock, Truck, CheckCircle, X, Users, ShoppingBag, Download, Power, CreditCard, UtensilsCrossed } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format date to Riga timezone
const formatRigaTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    timeZone: 'Europe/Riga',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const AdminPanel = () => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingOrders, setExportingOrders] = useState(false);
  const [orderRange, setOrderRange] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { from: today.slice(0, 8) + "01", to: today };
  });
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });

  useEffect(() => {
    const savedAuth = localStorage.getItem("admin_auth");
    if (savedAuth) {
      setAuthenticated(true);
      fetchData();
    }
  }, []);

  const handleLogin = () => {
    if (credentials.username && credentials.password) {
      localStorage.setItem("admin_auth", btoa(`${credentials.username}:${credentials.password}`));
      setAuthenticated(true);
      fetchData();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const auth = localStorage.getItem("admin_auth");
      const authHeader = { Authorization: `Basic ${auth}` };

      const [ordersRes, statsRes, usersRes, settingsRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: authHeader }),
        axios.get(`${API}/admin/stats`, { headers: authHeader }),
        axios.get(`${API}/admin/users`, { headers: authHeader }),
        axios.get(`${API}/admin/settings`, { headers: authHeader }),
        axios.get(`${API}/categories`)
      ]);

      setOrders(ordersRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSettings(settingsRes.data);
      setCategories(catRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_auth");
        setAuthenticated(false);
        toast.error("Authentication failed");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const auth = localStorage.getItem("admin_auth");
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { order_status: newStatus },
        { headers: { Authorization: `Basic ${auth}` } }
      );
      
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateSettings = async (patch) => {
    setSettingsSaving(true);
    try {
      const auth = localStorage.getItem("admin_auth");
      const res = await axios.patch(
        `${API}/admin/settings`,
        patch,
        { headers: { Authorization: `Basic ${auth}` } }
      );
      setSettings(res.data);
      toast.success("Настройки сохранены");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Не удалось сохранить настройки");
    } finally {
      setSettingsSaving(false);
    }
  };

  const downloadCsv = async (url, filename, setBusy) => {
    setBusy(true);
    try {
      const auth = localStorage.getItem("admin_auth");
      const res = await axios.get(url, {
        headers: { Authorization: `Basic ${auth}` },
        responseType: "blob"
      });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("CSV скачан");
    } catch (error) {
      toast.error("Не удалось экспортировать");
    } finally {
      setBusy(false);
    }
  };

  const exportUsersCsv = () =>
    downloadCsv(`${API}/admin/users/export`, `users_${new Date().toISOString().slice(0, 10)}.csv`, setExporting);

  const exportOrdersCsv = () => {
    if (!orderRange.from || !orderRange.to) {
      toast.error("Выберите период");
      return;
    }
    if (orderRange.to < orderRange.from) {
      toast.error("Дата «по» раньше даты «с»");
      return;
    }
    downloadCsv(
      `${API}/admin/orders/export?date_from=${orderRange.from}&date_to=${orderRange.to}`,
      `orders_${orderRange.from}_${orderRange.to}.csv`,
      setExportingOrders
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: "New", color: "bg-blue-500", icon: Package },
      preparing: { label: "Preparing", color: "bg-yellow-500", icon: Clock },
      ready: { label: "Ready", color: "bg-purple-500", icon: CheckCircle },
      delivering: { label: "Delivering", color: "bg-orange-500", icon: Truck },
      completed: { label: "Completed", color: "bg-green-500", icon: CheckCircle },
      cancelled: { label: "Cancelled", color: "bg-red-500", icon: X }
    };

    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold mb-6 text-center">Admin Login</h1>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full p-2 border rounded"
            />
            <input
              type="password"
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              onKeyPress={(e) => e.key === "Enter" && handleLogin()}
              className="w-full p-2 border rounded"
            />
            <Button onClick={handleLogin} className="w-full">
              Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Roze di Napoli - Admin Panel</h1>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Site Settings */}
        {settings && (
          <Card className="p-6 mb-8" data-testid="admin-settings-card">
            <h2 className="text-lg font-bold mb-4">Настройки сайта</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`flex items-center justify-between gap-4 p-4 rounded-lg border ${settings.orders_paused ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                <div className="flex items-center gap-3">
                  <Power className={`w-5 h-5 ${settings.orders_paused ? "text-red-600" : "text-green-600"}`} />
                  <div>
                    <div className="font-semibold">Приём заказов</div>
                    <div className="text-sm text-gray-600" data-testid="orders-status-text">
                      {settings.orders_paused ? "Пауза — заказы не принимаются" : "Включён — заказы принимаются"}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={!settings.orders_paused}
                  disabled={settingsSaving}
                  onCheckedChange={(checked) => updateSettings({ orders_paused: !checked })}
                  data-testid="orders-toggle"
                />
              </div>
              <div className={`flex items-center justify-between gap-4 p-4 rounded-lg border ${settings.card_payments_enabled ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
                <div className="flex items-center gap-3">
                  <CreditCard className={`w-5 h-5 ${settings.card_payments_enabled ? "text-green-600" : "text-gray-500"}`} />
                  <div>
                    <div className="font-semibold">Оплата картой онлайн</div>
                    <div className="text-sm text-gray-600" data-testid="card-status-text">
                      {settings.card_payments_enabled ? "Включена — наличные и карта" : "Выключена — только наличные"}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={settings.card_payments_enabled}
                  disabled={settingsSaving}
                  onCheckedChange={(checked) => updateSettings({ card_payments_enabled: checked })}
                  data-testid="card-payments-toggle"
                />
              </div>
            </div>
            <div className="mt-6">
              <AdminScheduleEditor settings={settings} saving={settingsSaving} onSave={updateSettings} />
            </div>
          </Card>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card className="p-4">
              <div className="text-sm text-gray-600">Total Orders</div>
              <div className="text-2xl font-bold">{stats.total_orders}</div>
            </Card>
            <Card className="p-4 bg-blue-50">
              <div className="text-sm text-gray-600">New</div>
              <div className="text-2xl font-bold text-blue-600">{stats.new_orders}</div>
            </Card>
            <Card className="p-4 bg-yellow-50">
              <div className="text-sm text-gray-600">Preparing</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.preparing}</div>
            </Card>
            <Card className="p-4 bg-orange-50">
              <div className="text-sm text-gray-600">Delivering</div>
              <div className="text-2xl font-bold text-orange-600">{stats.delivering}</div>
            </Card>
            <Card className="p-4 bg-green-50">
              <div className="text-sm text-gray-600">Completed</div>
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            </Card>
            <Card className="p-4 bg-purple-50">
              <div className="text-sm text-gray-600">Revenue</div>
              <div className="text-2xl font-bold text-purple-600">€{stats.total_revenue}</div>
            </Card>
          </div>
        )}

        {/* Tabs for Orders and Users */}
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="menu" className="flex items-center gap-2" data-testid="admin-tab-menu">
              <UtensilsCrossed className="w-4 h-4" />
              Меню
            </TabsTrigger>
          </TabsList>

          <TabsContent value="menu">
            <AdminMenuEditor categories={categories} />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="p-4 mb-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="orders-export-card">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">Период с</div>
                  <Input type="date" value={orderRange.from} onChange={(e) => setOrderRange({ ...orderRange, from: e.target.value })} data-testid="orders-export-from" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">по</div>
                  <Input type="date" value={orderRange.to} onChange={(e) => setOrderRange({ ...orderRange, to: e.target.value })} data-testid="orders-export-to" />
                </div>
              </div>
              <Button onClick={exportOrdersCsv} disabled={exportingOrders} variant="outline" data-testid="export-orders-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                {exportingOrders ? "Экспорт..." : "Экспорт заказов в CSV"}
              </Button>
            </Card>
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="font-bold text-lg">#{order.id.replace(/^#/, "").slice(0, 8).toUpperCase()}</div>
                      <div className="text-sm text-gray-600">{formatRigaTime(order.created_at)}</div>
                    </div>
                    {getStatusBadge(order.order_status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm font-semibold">Customer</div>
                      <div>{order.customer_name}</div>
                      <div className="text-sm text-gray-600">{order.customer_phone}</div>
                      {order.customer_email && (
                        <div className="text-sm text-gray-600">{order.customer_email}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Delivery Address</div>
                      <div className="text-sm">{order.delivery_address}</div>
                    </div>
                  </div>

                  <div className="border-t pt-4 mb-4">
                    <div className="text-sm font-semibold mb-2">Items:</div>
                    {order.items.map((item, idx) => (
                      <div key={idx} className="text-sm flex justify-between">
                        <span>{item.quantity}x {item.name}</span>
                        <span>€{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                      <span>Total:</span>
                      <span>€{order.total_amount.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Payment: {order.payment_method} ({order.payment_status})
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Select value={order.order_status} onValueChange={(value) => updateOrderStatus(order.id, value)}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="preparing">Preparing</SelectItem>
                        <SelectItem value="ready">Ready</SelectItem>
                        <SelectItem value="delivering">Delivering</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex justify-end mb-4">
              <Button onClick={exportUsersCsv} disabled={exporting || users.length === 0} variant="outline" data-testid="export-users-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? "Экспорт..." : "Экспорт в CSV"}
              </Button>
            </div>
            <div className="space-y-4">
              {users.length === 0 ? (
                <Card className="p-8 text-center text-gray-500">
                  Нет зарегистрированных пользователей
                </Card>
              ) : (
                users.map((user) => (
                  <Card key={user.id} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Имя</div>
                        <div className="font-medium">{user.full_name || user.name || 'Не указано'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Email</div>
                        <div className="text-sm">{user.email}</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Телефон</div>
                        <div className="text-sm">{user.phone || 'Не указан'}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Дата регистрации</div>
                        <div className="text-sm">{formatRigaTime(user.created_at)}</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Последний визит</div>
                        <div className="text-sm">{user.last_login ? formatRigaTime(user.last_login) : 'Никогда'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-600">Заказов</div>
                        <div className="text-sm font-bold text-green-600">{user.order_count || 0}</div>
                      </div>
                    </div>

                    {user.addresses && user.addresses.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="text-sm font-semibold text-gray-600 mb-2">Сохранённые адреса</div>
                        <div className="space-y-1">
                          {user.addresses.map((addr, idx) => (
                            <div key={idx} className="text-sm text-gray-700">
                              📍 {addr}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t">
                      <div className="text-xs text-gray-500 italic">
                        ℹ️ Пароли зашифрованы и не могут быть отображены (стандарт безопасности)
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
